import { useEffect, useRef } from 'react';
import { useHistory } from '@tanium/react-history-context';

/**
 * Attaches a listener similarly to `history.listen()` from
 * `@tanium/react-history-context`, but the listener will also fire for
 * `window.history` events which do not pass through the Tanium `history`.
 * Listening to these extra events is not always necessary, but is particularly
 * useful when dealing with history state since `pushState`/`replaceState` can
 * result in state changes without location changes.
 *
 * @param onChange Called whenever the location or history state changes.
 */
export const useHistoryListener = (onChange: () => void) => {
  const onChangeRef = useRef(onChange);
  const lastLocationHref = useRef(window.location.href);
  const history = useHistory();

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    // We subscribe to both popstate events and (Tanium) history events.
    // popstate events may be raised in some cases where history events are
    // not, e.g., user navigating back when the page location has not
    // changed, but the state has. However, popstate events fire before
    // history location has had a chance to update, which means the
    // component tree will likely not have been updated if we signal that
    // the store has changed immediately when a popstate event occurs. This
    // is especially noticeable if a component uses
    // history-location-provided query parameters to inform the initial
    // history state value, which would be stale at the time popstate fires.
    //
    // To mitigate this, we ignore popstate events that are expected to be
    // followed by history location changes (i.e., when the page location
    // has actually changed), and respond to popstate events immediately
    // otherwise.
    const handlePopState = () => {
      if (window.location.href !== lastLocationHref.current) {
        // Location changed, so defer signalling a store change until
        // history.listen fires
        lastLocationHref.current = window.location.href;
      } else {
        // popstate without location change means history listen won't fire,
        // so signal the store change immediately
        onChangeRef.current();
      }
    };

    window.addEventListener('popstate', handlePopState);
    const unsubscribe = history.listen(() => {
      onChangeRef.current();
    });

    return () => {
      window.removeEventListener('popstate', handlePopState);
      unsubscribe();
    };
  }, [history]);
};
